<template>
  <transition appear name="fade">
    <div class="d-flex flex-column">
      <div class="d-flex justify-content-between">
        <div id="space-filler"></div>
        <appAdminSidebar></appAdminSidebar>
        <appHeader></appHeader>
      </div>

      <div class="container p-0">
        <div v-if="isCardsLoading" class="text-center m-4 font-weight-bold">loading...</div>
        <div v-if="isCardsLoadError" class="text-center m-4 font-weight-bold">
          could not load list of cards
        </div>

        <div v-if="isCardsLoaded">
          <!-- Filters -->
          <div class="d-flex flex-column flex-md-row justify-content-between p-3">
            <div class="d-flex align-items-center mb-2 mb-md-0">
              <span class="mr-2">Number of cards to display:</span>
              <b-form-input v-model="limit" placeholder="Enter number" class="w-50" />
            </div>

            <div class="d-flex align-items-center">
              <span class="mr-2">Search:</span>
              <b-form-input v-model="searchCriteria" placeholder="Search name or order ID" class="w-100" />
            </div>
          </div>

          <!-- Card Table -->
          <div class="table-responsive custom-scroll-wrapper">
            <b-table
              :items="cards"
              :fields="cardFields"
              striped
              hover
              small
              class="cards-table"
            >
              <template #cell(name)="data">
                <span class="wrap-text">{{ data.item.name }}</span>
              </template>
              <template #cell(userEmail)="data">
                <div class="d-flex align-items-center">
                  <!-- Editing input -->
                  <div v-if="cardsEditing[data.item.code]" class="d-flex align-items-center w-100">
                    <b-form-input
                      v-model="newEmail"
                      size="sm"
                      type="email"
                      class="mr-2"
                      style="max-width: 200px;"
                    />
                    <b-button
                      variant="success"
                      size="sm"
                      class="mr-1"
                      @click="updateEmail(data.item)"
                    >
                      <font-awesome-icon icon="check" />
                    </b-button>
                    <b-button
                      variant="danger"
                      size="sm"
                      @click="cardsEditing[data.item.code] = false"
                    >
                      <font-awesome-icon icon="undo" />
                    </b-button>
                  </div>

                  <!-- Static email with edit icon -->
                  <div v-else class="d-flex align-items-center w-100 justify-content-between">
                    <span class="wrap-text">{{ data.item.userEmail }}</span>
                    <b-button
                      variant="link"
                      size="sm"
                      class="p-0 ml-2"
                      @click="enableEmailEdit(data.item)"
                    >
                      <font-awesome-icon icon="edit" />
                    </b-button>
                  </div>
                </div>
              </template>


              <template #cell(amount)="data">
                {{ data.item.currency }} {{ data.item.value }}
              </template>
              <!-- Details Button -->
              <template #cell(details)="data">
                <b-button size="sm" @click="openDetails(data.item)">View Details</b-button>
              </template>

              <!-- Actions -->
              <template #cell(actions)="data">
                <b-button
                  size="sm"
                  variant="danger"
                  class="mr-1"
                  @click="cancelCard(data.item.code)"
                  :disabled="!isCardCancellable(data.item.status)"
                >
                  Cancel
                </b-button>
                <b-button
                  size="sm"
                  variant="info"
                  class="mr-1"
                  @click="resendEmail(data.item)"
                  :disabled="!isCardEmaileable(data.item.status)"
                >
                  Email
                </b-button>
                <b-button
                  size="sm"
                  variant="primary"
                  @click="viewCard(data.item)"
                >
                  See
                </b-button>
              </template>
            </b-table>
          </div>
          <b-modal v-model="modalShow" hide-footer title="Status">
            {{ modalMessage }}
          </b-modal>

          <b-modal
            id="card-details-modal"
            ref="detailsModal"
            title="Card Details"
            hide-footer
            size="lg"
            centered
          >
            <div v-if="selectedCard">
              <table class="details-table">
                <tbody>
                  <tr v-for="(value, label) in formattedCardDetails" :key="label">
                    <th class="label-cell">
                      {{ label }}
                    </th>
                    <td class="value-cell">
                      <input
                        type="text"
                        class="readonly-field"
                        :value="value"
                        readonly
                        disabled
                        tabindex="-1"
                        aria-readonly="true"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-modal>
        </div>
        <appFooter />
      </div>
    </div>
  </transition>
</template>


<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { mapState } from "vuex";
import AdminSidebar from "@/views/admin/AdminSidebar";
import CardListItem from "@/components/CardListItem.vue";
import { changeEmail, emailCardOwner, cancelCard } from "../../../api/admin";

export default {
  components: {
    appHeader: Header,
    appFooter: Footer,
    appAdminSidebar: AdminSidebar,
    CardListItem,
  },
  data() {
    return {
      limit: 20,
      canEditEmail: false,
      newEmail: "",
      cardsEditing: {},
      searchCriteria: "",
      selectedCard: null,
      cardFields: [
        { key: 'userEmail', label: 'Email', tdClass: 'col-wrap' },
        { key: 'name', label: 'Name', tdClass: 'col-wrap' },
        { key: 'amount', label: 'Amount' },
        { key: 'status', label: 'Status' },
        // { key: 'creationTimestamp', label: 'Creation Date' },
        {
          key: 'creationTimestamp',
          label: 'Creation Date',
          formatter: (value) => {
            if (!value) return 'N/A';
            const date = new Date(value);
            return date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            });
          }
        },
        {
          key: 'expirationTimestamp',
          label: 'Expiration Date',
          formatter: (value) => {
            if (!value) return 'N/A';
            const date = new Date(value);
            return date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            });
          }
        },
        { key: 'details', label: 'Details' },
        { key: 'actions', label: 'Actions' }
      ]

    };
  },
  computed: {
    ...mapState({
      currentPage: (state) => state.admin.currentCardsPage,
      cards: (state) => state.admin.cards,
      isCardsLoaded: (state) => state.admin.status.cards.initialized,
      isCardsLoading: (state) => state.admin.status.cards.refreshing,
      isCardsLoadError: (state) => state.admin.status.cards.error,
    }),
    formattedCardDetails() {
      if (!this.selectedCard) return {};

      const formatDate = (ts) => {
        if (!ts) return 'N/A';
        const date = new Date(ts);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        }); // e.g., "25 June 2025"
      };

      return {
        'Code': this.selectedCard.code,
        'User Name': this.selectedCard.userName,
        'Email': this.selectedCard.userEmail,
        'Name': this.selectedCard.name,
        'Amount': `${this.selectedCard.currency} ${this.selectedCard.value}`,
        'Status': this.selectedCard.status,
        'Order ID': this.selectedCard.orderId,
        'Created By': this.selectedCard.byWhom,
        'Creation Date': formatDate(this.selectedCard.creationTimestamp),
        'Expiration Date': formatDate(this.selectedCard.expirationTimestamp),
        'Reference ID': this.selectedCard.referenceOrderID,
        'Redemption Instructions': this.selectedCard.redemptionInstructions,
        'UTID': this.selectedCard.utid,
      };
    }
  },
  watch: {
    limit(newLimit, oldLimit) {
      this.$store.dispatch("admin/fetchCards", {
        page: this.currentPage,
        limit: newLimit,
        searchCriteria: this.searchCriteria,
      });
    },
    searchCriteria(newCriteria, oldCriteria) {
      this.$store.dispatch("admin/fetchCards", {
        page: this.currentPage,
        limit: this.limit,
        searchCriteria: newCriteria,
      });
    },
  },
  methods: {
    enableEmailEdit(card) {
      this.newEmail = card.userEmail;
      this.$set(this.cardsEditing, card.code, true);
    },
    async updateEmail(card) {
      if (this.newEmail === card.userEmail) {
        this.cardsEditing[card.code] = false;
        return;
      }

      try {
        await changeEmail(card.code, card.userEmail, this.newEmail);
        this.showSuccess("Email updated successfully", () => {
          this.cardsEditing[card.code] = false;
        });
      } catch (e) {
        console.error(e);
        this.showError("Failed to update email.");
      }
    },
    isCardCancellable(status) {
      return status === "PENDING";
    },
    isCardEmaileable(status) {
      return status === "PENDING" || status === "ACTIVE";
    },
    async cancelCard(code) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to cancel this card?',
        {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, Cancel',
          cancelTitle: 'No',
          centered: true,
          headerClass: 'py-2 px-3 border-bottom-0',
          footerClass: 'py-2 px-3 border-top-0',
          bodyClass: 'py-2 px-3',
          modalClass: 'rounded-lg shadow-sm'
        }
      );

      if (!confirmed) return;

      try {
        await cancelCard(code);
        this.showSuccess("Card Cancelled");
      } catch (e) {
        console.error(e);
        this.showError(`Error cancelling card: ${e.message}`);
      }
    },
    async resendEmail(card) {
      try {
        const resp = await emailCardOwner(card.code);

        if (resp.status !== 200) {
          this.showError(`Did not receive an OK response. Received: ${resp.status}`);
        } else {
          this.showSuccess("Email sent successfully");
        }
      } catch (e) {
        console.error(e);
        this.showError(`Error sending email: ${e.message}`);
      }
    },
    viewCard(card) {
      this.$router.push({
        name: "UserViewCard",
        params: { code: card.code },
      });
    },
    formatReadableDate(ts) {
      if (!ts) return 'N/A';
      const date = new Date(ts);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      }); // e.g., "25 June 2025"
    },
    openDetails(card) {
      this.selectedCard = card;
      console.log(card)
      this.$refs.detailsModal.show();
    },
    showSuccess: function (message, onClose) {
      this.$bvModal
        .msgBoxOk(message, {
          size: "sm",
          headerClass: "p-2 border-bottom-0",
          footerClass: "p-2 border-top-0",
          centered: true,
        })
        .then(() => {
          onClose();
        });
    },
    showError: function (message) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm",
        headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0",
        okVariant: "danger",
        centered: true,
      });
    },
  },
  created() {
    this.$store.dispatch("admin/fetchCards", {
      page: this.currentPage,
      limit: this.limit,
    });
  },
};
</script>

<style lang="scss">
.header {
  background-color: #f0f9ff;
  border-radius: 16px 16px 0 0;
}

#cardList {
  .header,
  .btn {
    font-weight: 700;
    text-transform: uppercase;
  }

  .btn {
    border-radius: 4px;
    font-size: 9px;
  }
}

.card-row:nth-child(even) {
  background-color: #fafafa;
}

.custom-scroll-wrapper {
  overflow-x: auto;
  position: relative; /* Ensure pseudo-element positions correctly */
}

.custom-scroll-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  width: 30px;
  height: 100%;
  background: linear-gradient(to left, #fff, transparent);
  pointer-events: none;
}

.cards-table thead th,
.cards-table tbody td {
  white-space: nowrap;
  vertical-align: middle;
}

.cards-table thead th {
  text-align: center;
}

.wrap-text {
  display: inline-block;
  white-space: normal !important;
  word-break: break-word;
  max-width: 180px;
  overflow-wrap: anywhere;
}

.col-wrap {
  max-width: 350px;
  white-space: normal !important;
  word-break: break-word;
}

/* Details table styling */
.details-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 14px;
}

.details-table th.label-cell {
  width: 180px;
  text-align: right;
  font-weight: 600;
  color: #222;
  vertical-align: middle;
  padding-right: 18px;
  white-space: nowrap;
}

.details-table td.value-cell {
  width: 100%;
  padding: 0;
  vertical-align: middle;
}

/* Stylish, consistent input fields */
.readonly-field {
  width: 100%;
  background: #f5f5f7;
  color: #2d2d2d;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 15px;
  pointer-events: none;
  white-space: normal;
  word-break: break-word;
  transition: background 0.2s;
}

</style>
